import { useEffect, useState, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";

// Components
import RegistrationForm from "components/molecules/RegistrationForm";

// Constants
import {
  STORAGE_CLICK_ID,
  STORAGE_UTM_CAMPAIGN,
  STORAGE_UTM_MEDIUM,
  STORAGE_UTM_SOURCE,
} from "constants/variables";

// Helpers
import { mappedError } from "helpers/errors";

// Hooks
import { useSocialAuth } from "hooks/useSocialAuth";
import { useAuthMethods } from "hooks/useAuthMethods";
import { useAnalytics } from "hooks/useAmplitude";

// Selectors
import * as authSelectors from "redux/auth/selectors";
import { useCatalogsStore } from "stores/catalogs";

// Types
import { RegisterRequestType } from "types/Auth";

// Utils
import { getClientId } from "utils/ga";
import { getLocalStorageItem } from "utils/localStorage";

// Url
import url from "url/index";
import { useLocalizationStore } from "stores/localization";
import { RegistartionBanner } from "components/atoms/RegistartionBanner";
import { useGTM } from "hooks/useGTM";

type RegistrationFormContainerProps = { isAgreementShow?: boolean };

const RegistrationFormContainer = ({
  isAgreementShow,
}: RegistrationFormContainerProps) => {
  // eslint-disable-next-line unused-imports/no-unused-vars
  const methods = useForm({
    defaultValues: {
      ageAgreement: true,
      policyAgreement: true,
      currencyCode: "",
    },
  });
  const [errorResponse, setErrorResponse] = useState("");

  const {
    telegramAuthError,
    // eslint-disable-next-line unused-imports/no-unused-vars
    setTelegramAuthError,
    isLoading,
    signUp,
    queryClient,
    openPopup,
    closePopup,
    location,
    dispatch,
    changeStringField,
    GET_POPUP_TYPES,
  } = useSocialAuth();

  useAuthMethods();

  const { trackEvent } = useAnalytics();
  const { watch } = methods;
  const formValues = watch();
  const { login, password } = useSelector(authSelectors.all);
  const languages = useCatalogsStore((state) => state.languages);
  const lang = useLocalizationStore((state) => state.language) ?? "EN";
  const GTM = useGTM();

  const registrationStartSent = useRef(false);

  useEffect(() => {
    if (!registrationStartSent.current) {
      GTM.onRegistrationStart();
      registrationStartSent.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(data) {
    try {
      const language =
        (
          languages?.find(
            (l) => l.isoCode.toLowerCase() === lang.toLowerCase(),
          ) || {}
        ).id || 0;
      const clickId = getLocalStorageItem(STORAGE_CLICK_ID);
      const utmMedium = getLocalStorageItem(STORAGE_UTM_MEDIUM);
      const utmSource = getLocalStorageItem(STORAGE_UTM_SOURCE);
      const utmCampaign = getLocalStorageItem(STORAGE_UTM_CAMPAIGN);
      const queryParams = new URLSearchParams(location.search);
      const rParam = queryParams.get("r");
      const urlLocation = window.location.href;

      const params: RegisterRequestType = {
        login: data.login,
        password: data.password,
        currencyCode: data.currencyCode || "",
        languageId: language,
        depositUrl: `${window.location.origin.replace(":3000", ".ru")}/?popup=deposit`,
        welcomeBonusUrl:
          window.location.origin.replace(":3000", ".ru") +
          url.routes.info.welcomeBonus(),
        gaClientId: getClientId(),
      };

      if (clickId) {
        params.clickId = clickId;
      }
      if (utmMedium) {
        params.utmMedium = utmMedium;
      }
      if (utmSource) {
        params.utmSource = utmSource;
      }
      if (utmCampaign) {
        params.utmCampaign = utmCampaign;
      }
      if (rParam) {
        params.r = rParam;
      }

      const response = await signUp(params);

      // eslint-disable-next-line sonarjs/no-collapsible-if
      if ("data" in response) {
        if (response.data && !isLoading) {
          if (response.data.statusCode === 400) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            setErrorResponse(response.data.message);
          } else {
            // Временно скрываем событие так как нет денег
            // trackEvent("registration_success", {
            //   eventPropertyKey: "registration_success",

            //   // eslint-disable-next-line camelcase
            //   registration_method: selectedMethod,
            //   // eslint-disable-next-line camelcase
            //   registration_timestamp: getTimeStamp(),
            //   // eslint-disable-next-line camelcase
            //   campaign_id: getCampainId(),
            // });

            queryClient.invalidateQueries({ queryKey: ["banners"] });
            GTM.registrationStatus(
              response.data.id,
              data.login,
              "success",
              "",
              urlLocation,
              clickId,
              utmMedium,
              utmSource,
              utmCampaign,
            );

            if (params.r) {
              // eslint-disable-next-line no-console
              GTM.onRegistrationReferral(
                params.r,
                urlLocation,
                response.data.id,
                data.login,
                data.currencyCode,
              );
            } else {
              GTM.registrationStatus(
                response.data.id,
                data.login,
                "success",
                "",
                urlLocation,
                clickId,
                utmMedium,
                utmSource,
                utmCampaign,
              );
            }
            closePopup();
            openPopup(GET_POPUP_TYPES.popup.deposit);
          }
        }
      }
      // eslint-disable-next-line sonarjs/no-collapsible-if
      if ("error" in response) {
        GTM.registrationStatus(
          data.id,
          data.login,
          "error",
          response?.error.toString(),
          urlLocation,
          clickId,
          utmMedium,
          utmSource,
          utmCampaign,
        );

        if (response.error) {
          const errors = mappedError(response.error);
          // eslint-disable-next-line no-plusplus
          for (let i = 0; i < errors.length; i++) {
            if (errors[i].name) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              methods.setError(errors[i].name, {
                type: "custom",
                message: errors[i].error,
              });
            } else {
              setErrorResponse((prev) => {
                if (prev !== "") {
                  return [...prev.split("; "), errors[i].error].join("; ");
                }
                return errors[i].error;
              });
            }
          }
        }
      }
    } catch (error) {
      const urlLocation = window.location.href;
      const clickId = getLocalStorageItem(STORAGE_CLICK_ID);
      const utmMedium = getLocalStorageItem(STORAGE_UTM_MEDIUM);
      const utmSource = getLocalStorageItem(STORAGE_UTM_SOURCE);
      const utmCampaign = getLocalStorageItem(STORAGE_UTM_CAMPAIGN);

      // eslint-disable-next-line no-console
      console.log("Register Error", error);

      let errorMessage = "Unknown error";
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      trackEvent("form_error", {
        eventPropertyKey: "form_error",
        // eslint-disable-next-line object-shorthand
        errorMessage: errorMessage,
        dataId: data.id,
        dataLogin: data.login,
      });

      GTM.registrationStatus(
        data.id,
        data.login,
        "error",
        errorMessage,
        urlLocation,
        clickId,
        utmMedium,
        utmSource,
        utmCampaign,
      );
    }
  }

  const handleChange = (value: string, { name }: { name: string }) => {
    dispatch(changeStringField({ field: name, value }));

    trackEvent("form_field_filled", {
      // eslint-disable-next-line camelcase
      form_id: name,
      // eslint-disable-next-line camelcase
      field_name: name,
      // eslint-disable-next-line camelcase
      field_value_length: value?.length ?? 0,
    });
  };

  const onSubmit = (data) => {
    submit(data);
  };

  return (
    <div>
      <div>
        <RegistartionBanner currencyCode={formValues.currencyCode} />
      </div>
      <FormProvider {...methods}>
        <RegistrationForm
          login={login}
          password={password}
          currencyCode={formValues.currencyCode}
          error={errorResponse}
          isAgreementShow={isAgreementShow}
          onChange={handleChange}
          // eslint-disable-next-line react/jsx-handler-names
          onSubmit={onSubmit}
        />
      </FormProvider>

      {telegramAuthError && (
        <div
          style={{
            width: "100%",
            color: "red",
            marginTop: 12,
            textAlign: "center",
          }}
        >
          {telegramAuthError}
        </div>
      )}
    </div>
  );
};

export default RegistrationFormContainer;
